import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import {
  compareCatalogTasks,
  isCategoryLevelTask,
  uniqueCategoriesInSortOrder,
} from '../utils/generalTasks';

const EMPTY_CATEGORY_KEY = '__uncategorized__';

function matchesCategory(task, categoryKey) {
  if (categoryKey === EMPTY_CATEGORY_KEY) return !(task.category || '').trim();
  return (task.category || '') === categoryKey;
}

function AddTaskDialog({
  open,
  onClose,
  catalogTasks = [],
  usedGeneralTaskIds = [],
  adding = false,
  onAddExisting,
  onAddCategoryOnly,
  onAddNewTask,
}) {
  const { t } = useTranslation();
  const [pickerStep, setPickerStep] = useState(1);
  const [categorySearch, setCategorySearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [extraCategories, setExtraCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [showNewTask, setShowNewTask] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPickerStep(1);
    setCategorySearch('');
    setSelectedCategory('');
    setExtraCategories([]);
    setNewCategoryName('');
    setShowNewCategory(false);
    setNewTaskName('');
    setShowNewTask(false);
  }, [open]);

  const usedIds = useMemo(() => new Set(usedGeneralTaskIds.map(Number)), [usedGeneralTaskIds]);

  const catalogCategories = useMemo(
    () => uniqueCategoriesInSortOrder(catalogTasks, EMPTY_CATEGORY_KEY),
    [catalogTasks]
  );

  const categoryOptions = useMemo(() => {
    const seen = new Set(catalogCategories.map((cat) => cat.toLowerCase()));
    const extras = extraCategories.filter((cat) => !seen.has(cat.toLowerCase()));
    return [...catalogCategories, ...extras];
  }, [catalogCategories, extraCategories]);

  const filteredCategories = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    if (!query) return categoryOptions;
    return categoryOptions.filter((cat) => {
      const label = cat === EMPTY_CATEGORY_KEY
        ? t('dailyWorkInput.step2.uncategorized', { defaultValue: 'No category (legacy)' })
        : cat;
      return label.toLowerCase().includes(query);
    });
  }, [categoryOptions, categorySearch, t]);

  const tasksInSelectedCategory = useMemo(() => {
    if (!selectedCategory) return [];
    return [...catalogTasks]
      .filter((task) => !isCategoryLevelTask(task))
      .filter((task) => matchesCategory(task, selectedCategory))
      .filter((task) => !usedIds.has(Number(task.id)))
      .sort(compareCatalogTasks);
  }, [catalogTasks, selectedCategory, usedIds]);

  const categoryOnlyAlreadyUsed = useMemo(() => {
    if (!selectedCategory || selectedCategory === EMPTY_CATEGORY_KEY) return false;
    return catalogTasks.some((task) => (
      isCategoryLevelTask(task)
      && (task.category || '') === selectedCategory
      && usedIds.has(Number(task.id))
    ));
  }, [catalogTasks, selectedCategory, usedIds]);

  const categoryLabel = (cat) => (
    cat === EMPTY_CATEGORY_KEY
      ? t('dailyWorkInput.step2.uncategorized', { defaultValue: 'No category (legacy)' })
      : cat
  );

  const goToSubcategories = () => {
    if (!selectedCategory) return;
    setPickerStep(2);
    setNewTaskName('');
    setShowNewTask(false);
  };

  const handleAddExtraCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;
    const existing = categoryOptions.find((cat) => cat.toLowerCase() === name.toLowerCase());
    setSelectedCategory(existing || name);
    if (!existing) {
      setExtraCategories((prev) => [...prev, name]);
    }
    setNewCategoryName('');
    setCategorySearch('');
    setShowNewCategory(false);
    setPickerStep(2);
    setNewTaskName('');
    setShowNewTask(false);
  };

  const handleAddNewTask = () => {
    const name = newTaskName.trim();
    if (!name || !selectedCategory || selectedCategory === EMPTY_CATEGORY_KEY || adding) return;
    onAddNewTask({ name, category: selectedCategory });
  };

  const canUseCategoryOnly = Boolean(
    selectedCategory
    && selectedCategory !== EMPTY_CATEGORY_KEY
    && !categoryOnlyAlreadyUsed
    && !adding
  );

  const listBoxSx = {
    maxHeight: 280,
    overflow: 'auto',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    mb: 1,
  };

  return (
    <Dialog
      open={open}
      onClose={adding ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>{t('dailyWorkInput.step2.addTask', { defaultValue: 'Add task' })}</DialogTitle>
      <DialogContent dividers>
        {pickerStep === 1 ? (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {t('dailyWorkInput.step2.pickCategoryTitle', { defaultValue: '1. Select a category' })}
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder={t('dailyWorkInput.step2.searchCategories', { defaultValue: 'Search categories' })}
              disabled={adding}
              sx={{ mb: 1 }}
            />
            <Box sx={listBoxSx}>
              {filteredCategories.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                  {t('dailyWorkInput.step2.noMatchingCategories', { defaultValue: 'No matching categories.' })}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filteredCategories.map((cat) => {
                    const selected = selectedCategory === cat;
                    return (
                      <ListItemButton
                        key={cat}
                        selected={selected}
                        onClick={() => setSelectedCategory(cat)}
                        onDoubleClick={() => {
                          setSelectedCategory(cat);
                          setPickerStep(2);
                        }}
                        disabled={adding}
                        sx={{
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            fontWeight: 700,
                          },
                          '&.Mui-selected:hover': {
                            bgcolor: 'action.selected',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {selected ? <CheckIcon color="primary" fontSize="small" /> : null}
                        </ListItemIcon>
                        <ListItemText
                          primary={categoryLabel(cat)}
                          primaryTypographyProps={{ fontWeight: selected ? 700 : 400 }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              disabled={adding}
              onClick={() => setShowNewCategory((v) => !v)}
              sx={{ display: 'inline-block', mb: 1 }}
            >
              {t('dailyWorkInput.step2.categoryNotOnList', { defaultValue: 'Category is not on the list' })}
            </Link>
            <Collapse in={showNewCategory}>
              <Box display="flex" gap={1} alignItems="center" sx={{ mt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  label={t('dailyWorkInput.step2.newCategory', { defaultValue: 'New category' })}
                  disabled={adding}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddExtraCategory();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddExtraCategory}
                  disabled={adding || !newCategoryName.trim()}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {t('dailyWorkInput.step2.addCategory', { defaultValue: 'Add' })}
                </Button>
              </Box>
            </Collapse>
          </>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {t('dailyWorkInput.step2.pickSubcategoryTitle', {
                category: categoryLabel(selectedCategory),
                defaultValue: '2. Select a subcategory in {{category}}',
              })}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip
                label={`${t('generalTasks.fields.category', { defaultValue: 'Category' })}: ${categoryLabel(selectedCategory)}`}
                color="primary"
                variant="outlined"
              />
              <Button size="small" onClick={() => setPickerStep(1)} disabled={adding}>
                {t('dailyWorkInput.step2.changeCategory', { defaultValue: 'Change' })}
              </Button>
            </Box>
            {selectedCategory !== EMPTY_CATEGORY_KEY && (
              <Box sx={{ mb: 2 }}>
                {categoryOnlyAlreadyUsed ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('dailyWorkInput.step2.categoryAlreadyAdded', { defaultValue: 'This category is already added for today' })}
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    underline="hover"
                    disabled={!canUseCategoryOnly}
                    onClick={() => onAddCategoryOnly(selectedCategory)}
                    sx={{ display: 'inline-block' }}
                  >
                    {t('dailyWorkInput.step2.useCategoryOnly', { defaultValue: 'Use this category only' })}
                  </Link>
                )}
              </Box>
            )}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('dailyWorkInput.step2.subcategories', { defaultValue: 'Subcategories' })}
            </Typography>
            <Box sx={listBoxSx}>
              {tasksInSelectedCategory.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                  {t('dailyWorkInput.step2.noSubcategories', {
                    defaultValue: 'No subcategories. Add a new one or use the category only.',
                  })}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {tasksInSelectedCategory.map((taskItem) => (
                    <ListItemButton
                      key={taskItem.id}
                      onClick={() => onAddExisting(taskItem.id)}
                      disabled={adding}
                    >
                      <ListItemText primary={taskItem.name} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
            {selectedCategory !== EMPTY_CATEGORY_KEY && (
              <Box sx={{ mt: 1 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  disabled={adding}
                  onClick={() => setShowNewTask((v) => !v)}
                  sx={{ display: 'inline-block', mb: 1 }}
                >
                  {t('dailyWorkInput.step2.addNewSubcategory', { defaultValue: 'Add a new subcategory' })}
                </Link>
                <Collapse in={showNewTask}>
                  <Box display="flex" gap={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={newTaskName}
                      onChange={(e) => setNewTaskName(e.target.value)}
                      label={t('dailyWorkInput.step2.newTaskName', { defaultValue: 'New task' })}
                      disabled={adding}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddNewTask();
                        }
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddNewTask}
                      disabled={adding || !newTaskName.trim()}
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      {t('dailyWorkInput.step2.addNewTask', { defaultValue: 'Add' })}
                    </Button>
                  </Box>
                </Collapse>
              </Box>
            )}
          </>
        )}

        {adding && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {pickerStep === 2 && (
          <Button onClick={() => setPickerStep(1)} disabled={adding}>
            {t('dailyWorkInput.step2.backToCategories', { defaultValue: 'Back' })}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={adding}>{t('common.cancel')}</Button>
        {pickerStep === 1 && (
          <Button
            variant="contained"
            onClick={goToSubcategories}
            disabled={adding || !selectedCategory}
          >
            {t('dailyWorkInput.step2.nextToSubcategory', { defaultValue: 'Next' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AddTaskDialog;
export { EMPTY_CATEGORY_KEY };
